/**
 * Links que são credencial pelo próprio caminho (#626): quem tem a URL paga,
 * troca o cartão ou renova no lugar do assinante. O rastreador de erros manda
 * `window.location.href` e o `endpoint` da chamada que falhou para
 * `POST /logs/errors`, que grava no `error_logs` e no BetterStack.
 *
 * Páginas do checkout: `/alterar-cartao/<token>`, `/cartao/<código>`,
 * `/renovar/<token>`, `/r/<código>` e `/pix/<pedido>`.
 * Rotas da API: `/renewals/<token>`, `/renewals/r/<código>`,
 * `/payments/pix/<pedido>`, `/payments/status/<pedido>` e
 * `/payments/change-card/link/<código>`.
 *
 * Só o segmento-credencial vira `[REDACTED]`; o resto fica para depurar.
 * `(?!r\/)`: em `/renewals/r/[REDACTED]` o `r/` opcional voltaria atrás e o
 * `r` viraria o "token".
 * O backend redige de novo na entrada (`backend/src/logs/sanitize.ts`, mesma
 * regra), porque versão antiga do checkout continua mandando.
 */
const REDACTED = '[REDACTED]';

const CREDENCIAL_EM_LINK =
  /(\/(?:alterar-cartao|cartao|renovar|r|pix)\/)(?!\[REDACTED\])[^/?#\s"'<>\\]+/gi;

// Uma barra só no começo (não `\/+`): sem âncora, `\/+` fica quadrático numa
// sequência longa de `/`. Arquivo `.js`/`.ts`/`.map` (stack) não é credencial.
const CREDENCIAL_DE_ROTA =
  /(\/(?:renewals\/+(?:r\/+)?|payments\/+(?:pix|status|change-card\/+link)\/+))(?!\[REDACTED\]|r\/|[\w.-]+\.(?:js|ts|map)(?=[:)\s'"]|$))[^/?#\s"'<>\\]+/gi;

export function redactLinkCredentials(text: string): string {
  return text
    .replace(CREDENCIAL_EM_LINK, `$1${REDACTED}`)
    .replace(CREDENCIAL_DE_ROTA, `$1${REDACTED}`);
}

/**
 * Nº do pedido AppMax para o log de erro: só o final (`…432`), como o backend
 * faz no PM2 e no `error_logs`. O número inteiro abre `/pix/<pedido>`.
 */
export function finalDoPedido(numero: string | number | null | undefined): string | undefined {
  if (numero === null || numero === undefined || numero === '') return undefined;
  return `…${String(numero).slice(-3)}`;
}

/**
 * Aplica `redactLinkCredentials` em toda string da estrutura (cópia).
 *
 * Objeto com `toJSON` vira o que o `JSON.stringify` mandaria (revisão do #626):
 * o AxiosError do `unhandledrejection` (`additionalInfo.reason`) sai sem
 * `response` (corpo e cabeçalhos) e sem `request`, e com a `stack`, que não é
 * enumerável; `URL` vira o `href`, não `{}`; `Date`, a string ISO.
 */
export function redactLinkCredentialsDeep<T>(
  value: T,
  depth = 0,
  seen = new WeakSet<object>(),
): T {
  if (typeof value === 'string') return redactLinkCredentials(value) as T;
  if (depth > 8 || value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return '[Circular]' as T;
  seen.add(value);

  const toJSON = (value as { toJSON?: unknown }).toJSON;
  if (typeof toJSON === 'function') {
    let json: unknown;
    try {
      json = toJSON.call(value);
    } catch {
      // O rastreador roda dentro do tratamento de erro: não pode lançar.
      return '[unserializable]' as T;
    }
    return redactLinkCredentialsDeep(json, depth + 1, seen) as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => redactLinkCredentialsDeep(item, depth + 1, seen)) as T;
  }
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    out[key] = redactLinkCredentialsDeep(val, depth + 1, seen);
  }
  return out as T;
}
